import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuth } from '@/hooks/useAuth'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { ref, uploadBytesResumable, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/config/firebase'
import { customerService } from '@/services/firebase'
import { WAREHOUSE_ORIGIN, BAHAMAS_ISLANDS } from '@/config/company'
import LoadingSpinner from '@/components/LoadingSpinner'
import { PlusIcon, TrashIcon, PhotoIcon, MapPinIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

interface ShipmentItem {
  description: string
  quantity: number
}

interface DeliveryAddress {
  street: string
  city: string
  state: string
  zipCode: string
  country: string
}

const EMPTY_ADDRESS: DeliveryAddress = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'Bahamas',
}

const isAddressComplete = (a: DeliveryAddress) => Boolean(a.street.trim() && a.city.trim())

const formatAddress = (a: DeliveryAddress) =>
  [a.street, a.city, a.state, a.zipCode, a.country].filter((p) => p && p.trim()).join(', ')

interface CustomerShipmentForm {
  trackingNumber: string
  items: ShipmentItem[]
  palletType?: 'full' | 'half' | ''
}

// Pallet pricing constants
const PALLET_PRICES = {
  full: 950,
  half: 500
} as const

// Image compression utility - optimized for fastest processing
const compressImage = (file: File, maxWidth: number = 600, quality: number = 0.65, onProgress?: (progress: number) => void): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d', { 
      willReadFrequently: false, // Optimize for write performance
      alpha: false // No alpha channel needed for photos
    })!
    const img = new Image()
    
    img.onerror = () => reject(new Error('Failed to load image'))
    
    img.onload = () => {
      try {
        onProgress?.(5)
        
        // Calculate new dimensions - aggressive compression for speed
        let { width, height } = img
        
        // Always resize to maxWidth for consistency and speed
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width)
          width = maxWidth
        }
        
        onProgress?.(15)
        
        canvas.width = width
        canvas.height = height
        
        // Fast rendering settings
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'low' // Faster rendering
        
        onProgress?.(25)
        
        // Draw and compress in one step
        ctx.drawImage(img, 0, 0, width, height)
        
        onProgress?.(35)
        
        // Use lower quality for faster compression
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            })
            onProgress?.(50)
            resolve(compressedFile)
          } else {
            resolve(file)
          }
        }, 'image/jpeg', quality)
      } catch (error) {
        reject(error)
      }
    }
    
    // Use createObjectURL for faster loading
    img.src = URL.createObjectURL(file)
  })
}

export default function CustomerNewShipment() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // Only the setter is used — the preview renders from `imagePreview`.
  const [, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [compressionProgress, setCompressionProgress] = useState(0)
  const [isCompressing, setIsCompressing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null)
  const [items, setItems] = useState<ShipmentItem[]>([
    { description: '', quantity: 1 }
  ])

  // Delivery address. Loaded from the customer's profile so repeat customers
  // confirm rather than retype; only shown as an editable form when missing or
  // when they choose to send somewhere else this once.
  const [address, setAddress] = useState<DeliveryAddress>(EMPTY_ADDRESS)
  const [hasSavedAddress, setHasSavedAddress] = useState(false)
  const [editingAddress, setEditingAddress] = useState(false)
  const [saveAddressToProfile, setSaveAddressToProfile] = useState(true)
  const [loadingProfile, setLoadingProfile] = useState(true)

  useEffect(() => {
    if (!user) return

    const loadSavedAddress = async () => {
      try {
        const customer = await customerService.getCustomerById(user.id)
        const saved = { ...EMPTY_ADDRESS, ...(customer?.address || {}) }
        setAddress(saved)

        if (isAddressComplete(saved)) {
          setHasSavedAddress(true)
        } else {
          // Nothing on file yet — open the fields so the first shipment
          // collects it, and default to saving it for next time.
          setEditingAddress(true)
        }
      } catch (err) {
        console.error('Could not load saved delivery address:', err)
        setEditingAddress(true)
      } finally {
        setLoadingProfile(false)
      }
    }

    loadSavedAddress()
  }, [user])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<CustomerShipmentForm>({
    defaultValues: {
      palletType: ''
    }
  })
  
  // Watch palletType to update price display
  const selectedPalletType = watch('palletType') as 'full' | 'half' | '' | undefined

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1 }])
  }

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index))
    }
  }

  const updateItem = (index: number, field: keyof ShipmentItem, value: string | number) => {
    const updatedItems = [...items]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    setItems(updatedItems)
  }

  const handleImageUpload = async (file: File) => {
    if (!user) {
      setError('You must be logged in to upload images.')
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, GIF)')
      return
    }

    // Validate file size (5MB limit for faster processing)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB. Please compress the image first.')
      return
    }

    try {
      setError('')
      setIsCompressing(true)
      setCompressionProgress(0)
      
      // Quick compression for files > 300KB (very aggressive threshold)
      let processedFile = file
      if (file.size > 300 * 1024) {
        processedFile = await compressImage(file, 600, 0.65, (progress) => {
          setCompressionProgress(Math.min(progress, 50)) // Compression is 0-50% of total progress
        })
      } else {
        setCompressionProgress(50)
      }
      
      setIsCompressing(false)
      setIsUploading(true)
      setCompressionProgress(50) // Start upload progress at 50%
      setSelectedImage(processedFile)
      
      // Generate preview immediately (non-blocking)
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string)
      }
      reader.readAsDataURL(processedFile)
      
      // Sanitize file name (remove spaces and special characters)
      const sanitizedFileName = processedFile.name
        .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
        .replace(/\s+/g, '_') // Replace spaces with underscore
        .toLowerCase()
      
      // Upload immediately to Firebase Storage with progress tracking
      const storageRef = ref(storage, `shipment-images/${user.id}/${Date.now()}-${sanitizedFileName}`)
      
      try {
        // Try uploadBytesResumable first for progress tracking
        const uploadTask = uploadBytesResumable(storageRef, processedFile)
        
        // Track upload progress in real-time
        uploadTask.on('state_changed', 
          (snapshot) => {
            // Calculate progress percentage
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            setUploadProgress(50 + (progress * 0.5)) // Upload is 50-100% of total (compression was 0-50%)
          },
          async (error) => {
            console.error('UploadBytesResumable error:', error)
            console.error('Error code:', error.code)
            console.error('Error message:', error.message)
            
            // If uploadBytesResumable fails (e.g., CORS issue), try uploadBytes as fallback
            if (error.code === 'storage/unknown' || error.message?.includes('CORS') || error.message?.includes('cors') || error.message?.includes('network')) {
              console.log('Trying fallback upload method (uploadBytes)...')
              try {
                setUploadProgress(60)
                const snapshot = await uploadBytes(storageRef, processedFile)
                setUploadProgress(90)
                const imageUrl = await getDownloadURL(snapshot.ref)
                setUploadedImageUrl(imageUrl)
                setUploadProgress(100)
                setIsUploading(false)
                console.log('Image uploaded successfully using fallback method:', imageUrl)
                
                setTimeout(() => {
                  setUploadProgress(0)
                  setCompressionProgress(0)
                }, 1500)
                return
              } catch (fallbackError) {
                console.error('Fallback upload also failed:', fallbackError)
                setError('Unable to upload image. Please check your internet connection and Firebase Storage configuration.')
                setIsCompressing(false)
                setIsUploading(false)
                setUploadProgress(0)
                setCompressionProgress(0)
                setSelectedImage(null)
                setImagePreview(null)
                setUploadedImageUrl(null)
                return
              }
            }
            
            // Handle other errors
            let errorMessage = 'Error uploading image. Please try again.'
            
            if (error.code === 'storage/unauthorized') {
              errorMessage = 'You do not have permission to upload images. Please check your account permissions.'
            } else if (error.code === 'storage/canceled') {
              errorMessage = 'Upload was canceled. Please try again.'
            } else if (error.code === 'storage/quota-exceeded') {
              errorMessage = 'Storage quota exceeded. Please contact support.'
            }
            
            setError(errorMessage)
            setIsCompressing(false)
            setIsUploading(false)
            setUploadProgress(0)
            setCompressionProgress(0)
            setSelectedImage(null)
            setImagePreview(null)
            setUploadedImageUrl(null)
          },
          async () => {
            try {
              // Upload completed
              const imageUrl = await getDownloadURL(uploadTask.snapshot.ref)
              setUploadedImageUrl(imageUrl)
              setUploadProgress(100)
              setIsUploading(false)
              console.log('Image uploaded successfully:', imageUrl)
              
              // Reset progress after a brief delay to show completion
              setTimeout(() => {
                setUploadProgress(0)
                setCompressionProgress(0)
              }, 1500)
            } catch (error) {
              console.error('Error getting download URL:', error)
              setError('Error getting image URL. Please try again.')
              setIsUploading(false)
            }
          }
        )
      } catch (error) {
        // Catch any synchronous errors during upload setup
        console.error('Error setting up upload:', error)
        setError('Error setting up image upload. Please try again.')
        setIsCompressing(false)
        setIsUploading(false)
        setUploadProgress(0)
        setCompressionProgress(0)
      }
      
    } catch (error) {
      // Catch synchronous errors (compression, validation, etc.)
      console.error('Error processing image:', error)
      setError('Error processing image. Please try again.')
      setIsCompressing(false)
      setIsUploading(false)
      setUploadProgress(0)
      setCompressionProgress(0)
      setSelectedImage(null)
      setImagePreview(null)
      setUploadedImageUrl(null)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('=== SHIPMENT FILE INPUT CHANGE EVENT ===')
    console.log('Event target:', e.target)
    console.log('Event target files:', e.target.files)
    console.log('Files length:', e.target.files?.length)
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      console.log('File found in input:', file)
      handleImageUpload(file)
    } else {
      console.log('No file selected in input')
      setSelectedImage(null)
      setImagePreview(null)
    }
  }

  const onSubmit = async (data: CustomerShipmentForm) => {
    if (!user) return

    setLoading(true)
    setError('')

    try {
      // Validate items
      const validItems = items.filter(item => item.description.trim())
      if (validItems.length === 0) {
        setError('Please add at least one item with description.')
        setLoading(false)
        return
      }

      // Check if image is still uploading
      if (isUploading) {
        setError('Please wait for the image to finish uploading.')
        setLoading(false)
        return
      }

      if (!isAddressComplete(address)) {
        setError('Please add a delivery address so we know where this is going.')
        setEditingAddress(true)
        setLoading(false)
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      // Use already uploaded image URL (instant - no upload delay!)
      const imageUrl = uploadedImageUrl || null

      // Create shipment document with simplified structure
      const shipmentData: any = {
        customerId: user.id,
        trackingNumber: data.trackingNumber,
        items: validItems,
        // `images` is the field the admin dashboard and detail screens read.
        // `imageUrl` is kept for shipments created before this change.
        images: imageUrl ? [imageUrl] : [],
        imageUrl: imageUrl,
        origin: { ...WAREHOUSE_ORIGIN },
        destination: {
          street: address.street.trim(),
          city: address.city.trim(),
          state: address.state.trim(),
          zipCode: address.zipCode.trim(),
          country: address.country.trim() || 'Bahamas',
        },
        status: 'pending',
        createdBy: user.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      // Only include palletType and price if a pallet type is selected
      if (data.palletType && (data.palletType === 'full' || data.palletType === 'half')) {
        shipmentData.palletType = data.palletType
        shipmentData.price = PALLET_PRICES[data.palletType]
      }

      await addDoc(collection(db, 'shipments'), shipmentData)

      // Remember the address so the next shipment is one less thing to type.
      if (saveAddressToProfile && (!hasSavedAddress || editingAddress)) {
        try {
          await customerService.updateCustomer(user.id, {
            address: shipmentData.destination,
          })
        } catch (err) {
          // Not worth failing the shipment over — it's already created.
          console.error('Could not save delivery address to profile:', err)
        }
      }

      setUploadProgress(100)
      
      reset()
      setItems([{ description: '', quantity: 1 }])
      setSelectedImage(null)
      setImagePreview(null)
      setUploadedImageUrl(null)
      setUploadProgress(0)
      setCompressionProgress(0)
      navigate('/portal/shipments')
    } catch (err) {
      console.error('Error creating shipment:', err)
      setError('Failed to create shipment. Please try again.')
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          {uploadProgress > 0 && (
            <div className="mt-4">
              <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                {uploadProgress < 30 ? 'Preparing upload...' :
                 uploadProgress < 70 ? 'Uploading image...' :
                 uploadProgress < 90 ? 'Processing...' :
                 uploadProgress < 100 ? 'Saving shipment...' :
                 'Complete!'}
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create New Shipment</h1>
          <p className="mt-2 text-gray-600">
            Add your shipment details and tracking information
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Delivery Address */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <MapPinIcon className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Delivering to</h2>
          </div>
          <p className="text-sm text-gray-600 mb-5">
            We collect your package at our Miami warehouse and deliver it to this address.
          </p>

          {loadingProfile ? (
            <div className="py-4">
              <LoadingSpinner />
            </div>
          ) : !editingAddress && hasSavedAddress ? (
            /* Saved address — the common case. One glance, one click. */
            <div className="flex items-start justify-between gap-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">{formatAddress(address)}</p>
                  <p className="mt-1 text-xs text-gray-500">Saved to your profile</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingAddress(true)
                  setSaveAddressToProfile(false)
                }}
                className="shrink-0 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {!hasSavedAddress && (
                <p className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                  This is your first shipment, so we need a delivery address. We&apos;ll remember
                  it for next time.
                </p>
              )}

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                    Street address *
                  </label>
                  <input
                    id="street"
                    type="text"
                    value={address.street}
                    onChange={(e) => setAddress({ ...address, street: e.target.value })}
                    className="input w-full"
                    placeholder="e.g. 12 Bay Street"
                  />
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    Town / settlement *
                  </label>
                  <input
                    id="city"
                    type="text"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    className="input w-full"
                    placeholder="e.g. Nassau"
                  />
                </div>

                <div>
                  <label htmlFor="island" className="block text-sm font-medium text-gray-700 mb-1">
                    Island
                  </label>
                  <select
                    id="island"
                    value={address.state}
                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Select an island</option>
                    {BAHAMAS_ISLANDS.map((island) => (
                      <option key={island} value={island}>
                        {island}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
                    Postal code <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    id="zip"
                    type="text"
                    value={address.zipCode}
                    onChange={(e) => setAddress({ ...address, zipCode: e.target.value })}
                    className="input w-full"
                    placeholder="P.O. Box N-0000"
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    id="country"
                    type="text"
                    value={address.country}
                    onChange={(e) => setAddress({ ...address, country: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={saveAddressToProfile}
                    onChange={(e) => setSaveAddressToProfile(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Save this address to my profile
                </label>

                {hasSavedAddress && (
                  <button
                    type="button"
                    onClick={() => setEditingAddress(false)}
                    className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline"
                  >
                    Use saved address
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Basic Shipment Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipment Information</h2>

          {/* Tracking Number */}
          <div className="mb-6">
            <label htmlFor="trackingNumber" className="block text-sm font-medium text-gray-700 mb-2">
              Tracking Number *
            </label>
            <p className="text-sm text-gray-500 mb-2">
              The tracking number from the store or carrier you ordered from — the one for the
              package arriving at our Miami warehouse.
            </p>
            <input
              type="text"
              id="trackingNumber"
              {...register('trackingNumber', { required: 'Tracking number is required' })}
              className="input w-full"
              placeholder="e.g. 1Z999AA10123456784"
            />
            {errors.trackingNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.trackingNumber.message}</p>
            )}
          </div>

          {/* Pallet Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Pallet Type (Optional)
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Select if your shipment uses a full or half pallet. Standard shipments without pallets are selected by default.
            </p>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  id="pallet-none"
                  type="radio"
                  value=""
                  {...register('palletType')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="pallet-none" className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                  <span>No Pallet (Standard Shipment)</span>
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="pallet-full"
                  type="radio"
                  value="full"
                  {...register('palletType')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="pallet-full" className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                  <span className="font-semibold">Full Pallet</span>
                  <span className="ml-2 text-green-600 font-bold">${PALLET_PRICES.full}</span>
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="pallet-half"
                  type="radio"
                  value="half"
                  {...register('palletType')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="pallet-half" className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                  <span className="font-semibold">Half Pallet</span>
                  <span className="ml-2 text-green-600 font-bold">${PALLET_PRICES.half}</span>
                </label>
              </div>
            </div>
            {selectedPalletType && (selectedPalletType === 'full' || selectedPalletType === 'half') && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Selected Price: </span>
                  <span className="text-lg font-bold text-green-600">
                    ${PALLET_PRICES[selectedPalletType]}
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Image Upload */}
          <div className="mb-6">
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
              Shipment Image (Optional)
            </label>
            <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
              uploadedImageUrl ? 'border-green-300 bg-green-50' : 
              isUploading || isCompressing ? 'border-blue-300 bg-blue-50' :
              'border-gray-300 hover:border-gray-400'
            }`}>
              <div className="space-y-1 text-center w-full">
                <PhotoIcon className={`mx-auto h-12 w-12 ${
                  uploadedImageUrl ? 'text-green-500' : 
                  isUploading || isCompressing ? 'text-blue-500' : 
                  'text-gray-400'
                }`} />
                {(isCompressing || isUploading) ? (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {isCompressing ? 'Compressing...' : 'Uploading...'}
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                        style={{ width: `${Math.max(compressionProgress, uploadProgress)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {isCompressing ? `${compressionProgress}%` : `${uploadProgress}%`} complete
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      {isUploading ? 'Image will be ready when you submit the form' : ''}
                    </p>
                  </div>
                ) : uploadedImageUrl ? (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-green-700 mb-1">✓ Image uploaded successfully</p>
                    <p className="text-xs text-gray-500">Ready to submit</p>
                  </div>
                ) : (
                  <>
                    <div className="flex text-sm text-gray-600 justify-center">
                      <label
                        htmlFor="image"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="image"
                          type="file"
                          accept="image/*"
                          onChange={handleFileInputChange}
                          disabled={isCompressing || isUploading}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 5MB (uploads immediately for faster submission)
                    </p>
                  </>
                )}
              </div>
            </div>
            
            {imagePreview && !isCompressing && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
                <img 
                  src={imagePreview} 
                  alt="Shipment preview" 
                  className="max-w-full h-32 object-contain border rounded-lg"
                />
                {uploadedImageUrl && (
                  <p className="text-xs text-green-600 mt-1">✓ Uploaded and ready</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Items Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Shipment Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="btn-secondary inline-flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Item
            </button>
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            What&apos;s in the package? A short description of each item is enough — it helps us
            clear customs faster.
          </p>

          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="flex items-end space-x-4 p-4 border rounded-lg">
                <div className="flex-1">
                  <label
                    htmlFor={`item-desc-${index}`}
                    className="block text-xs font-medium text-gray-500 mb-1"
                  >
                    Description
                  </label>
                  <input
                    id={`item-desc-${index}`}
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="e.g. Running shoes"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="w-24">
                  <label
                    htmlFor={`item-qty-${index}`}
                    className="block text-xs font-medium text-gray-500 mb-1"
                  >
                    Qty
                  </label>
                  <input
                    id={`item-qty-${index}`}
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                    min="1"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary inline-flex items-center"
          >
            {loading ? (
              <>
                <LoadingSpinner />
                <span className="ml-2">Creating Shipment...</span>
              </>
            ) : (
              'Create Shipment'
            )}
          </button>
        </div>
        </form>
      </div>
    </div>
  )
}